import React from 'react';
import PropTypes from "prop-types";
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { classnames } from '../../../../utils/classnames';
import ItemCardActions from './components/itemCardActions/itemCardActions';
import Icon from '../../ui/icon/icon';
import { Status } from '../../../ui/index';
import './itemCard.css';

export function CustomChildrenitemCard({children}) {
    return (
        <div>
            <div className='neo-item-card-infos-separator'/>
            {children}
        </div>
    )
}

export default function ItemCard({cardStatus, resale_status, status, type, title, subtitle, action, additionnalContent, children, withMargin = true, startDate, rateGroupName}) {
    const { t } = useTranslation();
    const [showMore, setShowMore] = React.useState(false);

    const resaleStatus = React.useMemo(() => {
        const resaleLabels = {
            on_sale: t('widget.item-card.resale.on_sale'),
            sale_in_progress: t('widget.item-card.resale.sale_in_progress'),
            sale_in_progress_cancel_if_not_sold: t('widget.item-card.resale.sale_in_progress_cancel_if_not_sold'),
            sold: t('widget.item-card.resale.sold'),
        };
        return resaleLabels[resale_status] || '';
    }, [resale_status]);

    const color = React.useMemo(() => {
        switch(status) {
            case 'ongoing':
            case 'valid': // ticket status
                return 'green';
            case 'pending':
            case 'reservation': // ticket status
                return 'orange';
            case 'waiting_confirmation':
            case 'removed': // ticket status
                return 'red';
            case 'used':
            case 'expired':
            case 'canceled':
                return 'grey'
            default:
                return ''
        }
    }, [status]);

    const _infosAction = React.useMemo(() => {
        if (children) {
            return {
                label: t('widget.item-card.informations'),
                icon: "infoCircle",
                onClick: () => setShowMore(state => !state)
            }
        }
        return null;
    }, [children]);

    const _onClick = React.useMemo(() => {      
        return action?.enabled ? action.onClick : null;
    }, [action]);

    return (
        <div className={classnames('neo-item-card-container', {clickable: action, 'with-margin': withMargin})} onClick={_onClick}>
            <div className={classnames('neo-item-card')}>
                <div className='neo-item-card-left-container'>
                    <div className={classnames('neo-item-card-status-line', {'shorter': Boolean(resale_status)}, color)} />
                    <div>
                        <div className='neo-item-card-title'>{title}</div>
                        {startDate && (
                            <div className='neo-item-card-date'>
                                <Icon name="calendar" className='neo-item-card-icon'/> 
                                <div>
                                    {moment.utc(startDate).local().format('DD/MM/YYYY HH:mm')}
                                </div>
                            </div>
                        )}
                        <div className='neo-item-card-subtitle'>{subtitle}</div>
                        {rateGroupName && <div className='neo-item-rate-group'>{rateGroupName}</div>}
                        {status && <Status color={color} text={t(`widget.${type}.status.${status}`)}/>}
                        {resale_status && <div className='neo-item-card-resale-status'><Icon name="transfer"/> <div>{resaleStatus}</div></div>}
                    </div>
                </div>
                <div className='neo-item-card-right-container'>
                    <ItemCardActions mainAction={action} infosAction={_infosAction} status={cardStatus}/>
                </div>
            </div>
            {additionnalContent}
            {showMore && <CustomChildrenitemCard>{children}</CustomChildrenitemCard>}
        </div>
    )
}

ItemCard.defaultProps = {
    cardStatus: 'default',
    status: null,
    resale_status: null,
    type: 'item-card',
    title: '',
    subtitle: '',
    action: null,
    additionnalContent: null,
    startDate: null,
    rateGroupName: null
}

ItemCard.propTypes = {
    cardStatus: PropTypes.oneOf(['default', 'warning', 'error']),
    status: PropTypes.string,
    resale_status: PropTypes.string,
    type: PropTypes.string,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    action: PropTypes.object,
    additionnalContent: PropTypes.node,
    startDate: PropTypes.string,
    rateGroupName: PropTypes.string,
};